import React from 'react';
import { clsx } from 'clsx';

export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type SpinnerVariant = 'primary' | 'secondary' | 'white' | 'current';

export interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: SpinnerSize;
  variant?: SpinnerVariant;
  label?: string;
}

const sizeStyles: Record<SpinnerSize, string> = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
  xl: 'w-12 h-12',
};

const variantStyles: Record<SpinnerVariant, string> = {
  primary: 'text-primary-600',
  secondary: 'text-neutral-600',
  white: 'text-white',
  current: 'text-current',
};

export const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({ size = 'md', variant = 'primary', label, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        role="status"
        aria-label={label || 'Loading'}
        className={clsx('inline-flex items-center justify-center', className)}
        {...props}
      >
        <svg
          className={clsx('animate-spin', sizeStyles[size], variantStyles[variant])}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        {label && <span className="sr-only">{label}</span>}
      </div>
    );
  }
);

Spinner.displayName = 'Spinner';

// Full page loading spinner
export const PageSpinner: React.FC<{ message?: string }> = ({ message = 'Loading...' }) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
      <Spinner size="xl" />
      <p className="mt-4 text-neutral-500 text-sm">{message}</p>
    </div>
  );
};

PageSpinner.displayName = 'PageSpinner';

// Overlay spinner for blocking interactions
export const OverlaySpinner: React.FC<{ message?: string }> = ({ message }) => {
  return (
    <div className="fixed inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center z-50">
      <Spinner size="xl" />
      {message && <p className="mt-4 text-neutral-600 font-medium">{message}</p>}
    </div>
  );
};

OverlaySpinner.displayName = 'OverlaySpinner';

// Inline loading indicator with text
export interface LoadingIndicatorProps {
  loading?: boolean;
  children?: React.ReactNode;
  spinnerSize?: SpinnerSize;
  text?: string;
}

export const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({
  loading = true,
  children,
  spinnerSize = 'sm',
  text,
}) => {
  if (!loading) return <>{children}</>;

  return (
    <span className="inline-flex items-center gap-2">
      <Spinner size={spinnerSize} variant="current" />
      {text && <span>{text}</span>}
    </span>
  );
};

LoadingIndicator.displayName = 'LoadingIndicator';

export default Spinner;

